import { amorphicStatic } from '@haventech/amorphic';

import * as querystring from 'querystring';
import requestPromise from 'request-promise-native';
import { Auth0Channel, Auth0ClientInfo, Auth0Configs, Auth0Utils, Auth0WellKnownOIDC } from './Auth0Utils';
import { Utils } from '../Utils';
import { serverOnly } from '../helpers';
import { CPLogger, Category, PiiLevelEnum } from '../logger/CPLogger';
import { LoggerSingleton } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

export enum Auth0RequestTypes {
  GET = 'GET',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
}

export enum Auth0APIEndpoints {
  OAUTH_TOKEN = '/oauth/token',
  USER_BY_EMAIL = '/api/v2/users-by-email',
  USER_UPDATE = '/api/v2/users',
  JOBS_VERIFICATION_EMAIL = '/api/v2/jobs/verification-email',
  WELLKNOWN_CONFIG = '/.well-known/openid-configuration',
}

export enum Auth0CallbackEndpoints {
  AUTH0_CALLBACK = '/auth0callback',
}

const DEFAULT_REQUEST_TIMEOUT: number = 1000 * 60 * 1;
const DEFAULT_RETRY_MAX: number = 1;

export class Auth0Client {
  @serverOnly()
  public static async getWellKnownConfigIssuer(auth0Domain: string): Promise<string> {
    const uri = `${auth0Domain}${Auth0APIEndpoints.WELLKNOWN_CONFIG}`;

    const options = {
      uri: uri,
      method: Auth0RequestTypes.GET,
      rejectUnauthorized: false,
      json: true,
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        name: 'content-type',
        value: 'application/json',
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    try {
      // this is the default .well-known/openid-configuration, we are only interested in the issuer
      const response: Auth0WellKnownOIDC = await requestPromise(options);

      if (response?.issuer) {
        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'getWellKnownConfigIssuer',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
            data: {
              uri,
              issuer: response.issuer,
            },
          },
          'Successfully retrieved issuer from auth0 domain well-known'
        );

        return response.issuer;
      } else {
        LoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Client',
            function: 'getWellKnownConfigIssuer',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
            data: {
              uri,
            },
          },
          'There was a problem retrieving issuer from auth0 domain well-known'
        );

        throw new Error(`Unable to exchange code for token`);
      }
    } catch (error) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'Auth0Client',
          function: 'getWellKnownConfigIssuer',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          data: {
            uri,
            error: {
              error: error.error,
              code: error.code,
              description: error.error_description,
              method: error.method,
            },
          },
        },
        'There was a problem retrieving issuer from auth0 domain well-known'
      );

      throw new Error(`Unable to retrieve auth0 issuer`);
    }
  }

  @serverOnly()
  public static async exchangeCodeForToken(
    code: string,
    redirectUri,
    auth0Channel: Auth0Channel
  ): Promise<Auth0Response> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(auth0Channel);
    const clientInfo: Auth0ClientInfo = Auth0Utils.getAuth0ClientInfo(auth0Channel);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.OAUTH_TOKEN}`;

    const options = {
      uri: uri,
      method: Auth0RequestTypes.POST,
      rejectUnauthorized: false,
      json: true,
      body: {
        grant_type: 'authorization_code',
        client_id: clientInfo.clientId,
        client_secret: clientInfo.clientSecret,
        code: code,
        redirect_uri: redirectUri + Auth0CallbackEndpoints.AUTH0_CALLBACK,
        connection: auth0Conf.connection,
      },
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        name: 'content-type',
        value: 'application/json',
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    try {
      const response: Auth0Response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Client',
          function: 'exchangeCodeForToken',
          category: Category.request,

          data: {
            uri: uri,
            partialCode: code.slice(-4),
          },
        },
        'Successfully exchanged code for token'
      );

      return response;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Client',
          function: 'exchangeCodeForToken',
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              uri: uri,
              partialCode: code.slice(-4),
            },
          },
        },
        'There was a problem exchanging Auth0 code for token'
      );

      throw new Error(`Unable to exchange code for token`);
    }
  }

  @serverOnly()
  public static async authenticate(username: string, password: string): Promise<Auth0Response> {
    const auth0Secret: string = Auth0Utils.getAuth0Secret(Auth0Channel.HAVEN);
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);

    const lowerCaseUsername = username.toLowerCase();

    const options = {
      uri: `${auth0Conf.domain}${Auth0APIEndpoints.OAUTH_TOKEN}`,
      method: Auth0RequestTypes.POST,
      rejectUnauthorized: false,
      json: true,
      body: {
        grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
        username: lowerCaseUsername,
        password: password,
        client_id: auth0Conf.clientId,
        client_secret: auth0Secret,
        realm: auth0Conf.connection,
      },
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        name: 'content-type',
        value: 'application/json',
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    try {
      const response: Auth0Response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Client',
          function: 'authenticate',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.s2 },
          data: {
            username: lowerCaseUsername,
          },
        },
        'Succesfully authenticated user'
      );

      return response;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Client',
          function: 'authenticate',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.s2 },
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              username: lowerCaseUsername,
            },
          },
        },
        'There was an issue authenticating user'
      );

      throw error.error;
    }
  }

  public static async setEmailVerified(auth0UserId: string): Promise<Auth0Response> {
    const updateObj = {
      email_verified: true,
    };
    return this.updateUserData(auth0UserId, updateObj, 'setEmailVerified', 'email verified flag');
  }

  public static async setMfaEmailSent(auth0UserId: string): Promise<Auth0Response> {
    const updateObj = {
      user_metadata: {
        mfa_email_sent: true,
      },
    };
    return this.updateUserData(auth0UserId, updateObj, 'setMfaEmailSent', 'mfa email sent flag');
  }

  public static setRandomTemporaryPassword(auth0UserId: string): Promise<Auth0Response> {
    const updateObj = {
      password: Utils.generateUuidV4(),
    };
    return this.updateUserData(auth0UserId, updateObj, 'setRandomTemporaryPassword', 'password');
  }

  @serverOnly()
  private static async updateUserData(
    auth0UserId: string,
    updateObj: object,
    activity: string,
    updateDesc: string
  ): Promise<Auth0Response> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0UserId}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.PATCH,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          body: {
            connection: auth0Conf.connection,
          },
          timeout: DEFAULT_REQUEST_TIMEOUT,
          headers: {
            name: 'content-type',
            value: 'application/json',
            ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
          },
        };

        Object.assign(options.body, updateObj);

        const response: Auth0Response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: activity,
            category: Category.request,

            data: {
              auth0UserId: auth0UserId,
              uri: uri,
            },
          },
          `Succesfully updated ${updateDesc} in Auth0`
        );
        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: activity,
            category: Category.request,

            data: {
              auth0UserId: auth0UserId,
              uri: uri,
            },
          },
          `Succesfully updated ${updateDesc} in Auth0`
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: activity,
            category: Category.request,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                auth0UserId: auth0UserId,
                uri: uri,
              },
            },
          },
          `Unable to update ${updateDesc} in Auth0`
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry

          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: activity,
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error(`Unable to update ${updateDesc} in Auth0`);
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async updateEmail(auth0UserId: string, newEmail: string): Promise<Auth0Response> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0UserId}`;
    const lowerCaseEmail = newEmail.toLowerCase();

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.PATCH,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          body: {
            email: lowerCaseEmail,
            email_verified: true,
            connection: auth0Conf.connection,
          },
          timeout: DEFAULT_REQUEST_TIMEOUT,
          headers: {
            name: 'content-type',
            value: 'application/json',
            ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
          },
        };

        const response: Auth0Response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'updateEmail',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              auth0UserId: auth0UserId,
              uri: uri,
              newEmail: lowerCaseEmail,
            },
          },
          'Succesfully updated email in Auth0'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'updateEmail',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                auth0UserId: auth0UserId,
                uri: uri,
                newEmail: lowerCaseEmail,
              },
            },
          },
          'Unable to update Auth0 Email'
        );
        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'updateEmail',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error(`Unable to update new email`);
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async createAccount(email: string, password: string): Promise<Auth0Response> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}`;
    const lowerCaseEmail = email.toLowerCase();

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.POST,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          body: {
            connection: auth0Conf.connection,
            email: lowerCaseEmail,
            password: password,
          },
          timeout: DEFAULT_REQUEST_TIMEOUT,
          headers: {
            name: 'content-type',
            value: 'application/json',
            ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
          },
        };

        const response: Auth0Response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'createAccount',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              uri: uri,
              email: lowerCaseEmail,
            },
          },
          'Successfully created email in Auth0'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Client',
            function: 'createAccount',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                email: lowerCaseEmail,
              },
            },
          },
          'Unable to create Auth0 Email'
        );
        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'createAccount',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw error;
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async resendVerificationEmail(
    auth0UserId: string,
    email: string,
    channel = Auth0Channel.HAVEN
  ): Promise<Auth0Response> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(channel);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(channel);
    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.JOBS_VERIFICATION_EMAIL}`;
    const lowerCaseEmail = email.toLowerCase();

    if (!auth0UserId) {
      const accountInfo = await Auth0Client.getAccountInfo(lowerCaseEmail, undefined);
      auth0UserId = accountInfo.user_id;
    }

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.POST,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          body: {
            user_id: auth0UserId,
            client_id: auth0Conf.clientId,
          },
          timeout: DEFAULT_REQUEST_TIMEOUT,
          headers: {
            name: 'content-type',
            value: 'application/json',
            ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
          },
        };

        const response: Auth0Response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'resendVerificationEmail',
            category: Category.request,

            data: {
              auth0UserId: auth0UserId,
              uri: uri,
            },
          },
          'Succesfully submitted resend verification email request'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'resendVerificationEmail',
            category: Category.request,
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                auth0UserId: auth0UserId,
                uri: uri,
              },
            },
          },
          'There was an issue resending verification email'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'resendVerificationEmail',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error(`Unable to submit Auth0 resend verification email request`);
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async acquireNewAPIToken(channel: Auth0Channel): Promise<string> {
    const auth0Secret: string = Auth0Utils.getAuth0APIClientSecret(channel);
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(channel);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.OAUTH_TOKEN}`;

    const options = {
      uri: uri,
      method: Auth0RequestTypes.POST,
      rejectUnauthorized: false,
      json: true,
      body: {
        grant_type: 'client_credentials',
        client_id: auth0Conf.apiClientId,
        client_secret: auth0Secret,
        audience: auth0Conf.audience,
        scope: auth0Conf.apiScope,
      },
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        name: 'content-type',
        value: 'application/json',
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    try {
      const response: Auth0Response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Client',
          function: 'acquireNewAPIToken',
          category: Category.request,

          data: {
            uri: uri,
          },
        },
        'Successfully acquired new access token'
      );

      return response.access_token;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Client',
          function: 'acquireNewAPIToken',
          category: Category.request,
          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              uri: uri,
            },
          },
        },
        'There was an issue acquiring new API Token'
      );

      throw new Error(`Unable to acquire new API token`);
    }
  }

  @serverOnly()
  public static async getAccountInfo(email: string, channel = Auth0Channel.HAVEN): Promise<Auth0ResponseAccount> {
    if (!email) {
      return null;
    }

    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(channel);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(channel);

    const cleanedEmail = querystring.escape(email);
    const lowerCaseEmail = cleanedEmail.toLowerCase();
    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_BY_EMAIL}?email=${lowerCaseEmail}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.GET,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'getAccountInfo',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              uri: uri,
              email: lowerCaseEmail,
            },
          },
          'Succesfully acquired account information'
        );

        return response[0];
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'getAccountInfo',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                email: lowerCaseEmail,
              },
            },
          },
          'There was an issue getting Auth0 account info'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'getAccountInfo',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error(`Unable to get auth0 account info`);
        }
      }
      //only want to retry once.
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async createVerifiedAccount(email: string, password: string): Promise<any> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}`;

    let retryCount = 0;

    do {
      try {
        // to verify or to not verify?!?!?!?!?!
        const options = {
          uri: uri,
          method: Auth0RequestTypes.POST,
          auth: {
            bearer: auth0APIToken,
          },
          body: {
            email: email,
            password: password,
            connection: auth0Conf.connection,
            email_verified: true,
            verify_email: false,
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'createVerifiedAccount',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              uri: uri,
              email: email,
            },
          },
          'Succesfully created verified Auth0 User'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'createVerifiedAccount',
            category: Category.request,
            context: { piiLevel: PiiLevelEnum.s2 },
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                email: email,
              },
            },
          },
          'There was an issue getting creating Auth0 verified account'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'createVerifiedAccount',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          throw new Error('Unable to create Auth0 verified user');
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async deleteAuth0User(auth0Id: string, channel?: Auth0Channel): Promise<any> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(channel);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(channel);

    // may change depending on channel
    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0Id}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.DELETE,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'deleteAuth0User',
            category: Category.request,

            data: {
              uri: uri,
              auth0Id: auth0Id,
            },
          },
          'Succesfully deleted Auth0 User'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'deleteAuth0User',
            category: Category.request,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                auth0Id: auth0Id,
              },
            },
          },
          'There was an issue getting Auth0 account info'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'deleteAuth0User',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(channel);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(channel);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error('Unable to delete Auth0 user');
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async linkAuth0User(auth0Id: string, linkingAuth0Id: string): Promise<any> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0Id}/identities`;

    let retryCount = 0;

    do {
      try {
        const splitLinkingAuth0Id = linkingAuth0Id.split('|');
        const options = {
          uri: uri,
          method: Auth0RequestTypes.POST,
          auth: {
            bearer: auth0APIToken,
          },
          body: {
            provider: splitLinkingAuth0Id[0],
            user_id: splitLinkingAuth0Id[1],
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'linkAuth0User',
            category: Category.request,

            data: {
              uri: uri,
              primaryAuth0Id: auth0Id,
              linkingAuth0Id: linkingAuth0Id,
            },
          },
          'Succesfully linked Auth0 User'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'linkAuth0User',
            category: Category.request,
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                auth0Id: auth0Id,
              },
            },
          },
          'There was an issue linking the Auth0 user to primary account'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'linkAuth0User',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error('Unable to link Auth0 user');
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async unlinkAuth0User(
    auth0Id: string,
    unlinkingAuth0Id: string,
    unlinkingProvider: string
  ): Promise<any> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0Id}/identities/${unlinkingProvider}/${unlinkingAuth0Id}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.DELETE,
          auth: {
            bearer: auth0APIToken,
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'unlinkAuth0User',
            category: Category.request,

            data: {
              uri: uri,
              primaryAuth0Id: auth0Id,
              unlinkingAuth0Id: unlinkingAuth0Id,
              unlinkingProvider: unlinkingProvider,
            },
          },
          'Succesfully unlinked Auth0 User'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'unlinkAuth0User',
            category: Category.request,
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                auth0Id: auth0Id,
                unlinkingAuth0Id: unlinkingAuth0Id,
                unlinkingProvider: unlinkingProvider,
              },
            },
          },
          'There was an issue unlinking the Auth0 user to primary account'
        );

        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'unlinkAuth0User',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error('Unable to unlink Auth0 user');
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  @serverOnly()
  public static async getAccountInfoById(auth0Id: string): Promise<any> {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.HAVEN);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.HAVEN);

    const uri = `${auth0Conf.domain}${Auth0APIEndpoints.USER_UPDATE}/${auth0Id}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: Auth0RequestTypes.GET,
          auth: {
            bearer: auth0APIToken,
          },
          body: {
            user_id: auth0Id,
          },
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
        };

        const response = await requestPromise(options);

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'getAccountInfoById',
            category: Category.request,

            data: {
              uri: uri,
              auth0Id: auth0Id,
            },
          },
          'Succesfully fetched auth0 user'
        );

        return response;
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'getAccountInfoById',
            category: Category.request,
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                uri: uri,
                auth0Id: auth0Id,
              },
            },
          },
          'There was an issue fetching the auth0 user'
        );
        if (error.error.error === 'Unauthorized' && retryCount < DEFAULT_RETRY_MAX) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: 'getAccountInfoById',
              category: Category.request,
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.HAVEN);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.HAVEN);
          retryCount = retryCount + 1;
        } else {
          //exit
          throw new Error('Unable to fetch Auth0 user');
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }

  public static async getAMCUserInfo(email: string): Promise<AMCAuth0ResponseUser> {
    if (!email) {
      return null;
    }

    const cleanedEmail = querystring.escape(email);
    const lowerCaseEmail = cleanedEmail.toLowerCase();
    const uriSuffix = `v1/AmcUser/search?email=${lowerCaseEmail}`;

    let response = null;

    try {
      response = await Auth0Client.sendAMCRequest(
        uriSuffix,
        Auth0RequestTypes.GET,
        'getAMCUserInfo',
        'get AMC Auth0 user info',
        true
      );
    } catch (error) {
      const apiError = error.error;
      if (apiError && apiError.status === 404 && apiError.message && apiError.message.includes('User not found')) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Auth0Client',
            function: 'getAMCUserInfo',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: {
              email: email,
            },
          },
          `No AMC user found for email`
        );
      } else {
        UILoggerSingleton.getInstance().error(
          {
            module: 'Auth0Client',
            function: 'getAMCUserInfo',
            category: UICategory.request,
            context: { piiLevel: UIPiiLevelEnum.s2 },
            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                email: email,
              },
            },
          },
          `Failed to lookup user in AMC`
        );
      }
    }

    return response;
  }

  public static async registerPhoneForMfa(auth0Id: string, phone: string, customerId: string): Promise<AMCResponse> {
    if (!phone) {
      return null;
    }

    const formattedPhone: string = phone.length === 11 ? phone.substring(1) : phone;
    const uriSuffix = `/v1/AmcUser/registerphone?user_id=${auth0Id}&phone=${formattedPhone}&cvp_id=${customerId}`;

    const response = await Auth0Client.sendAMCRequest(
      uriSuffix,
      'PUT',
      'registerPhoneForMfa',
      'register phone number for MFA'
    );

    return response;
  }

  @serverOnly()
  public static async updateAMCEmail(email: string, token: string): Promise<AMCAccountServiceResponse> {
    const domain: string = amorphicStatic.config.nconf.get('amcAccountServiceDomain');
    const uri: string = `${domain}/api/email`;

    const options = {
      uri: uri,
      method: 'PUT',
      json: true,
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        'x-access-token': token,
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
      body: {
        new_email: email,
      },
    };

    try {
      const response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Client',
          function: 'updateAMCEmail',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.s1 },
          data: {
            email: email,
            token: token,
          },
        },
        `Sent request to AMC to update email`
      );

      return response;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Client',
          function: 'updateAMCEmail',
          category: Category.request,
          error: {
            name: error.name,
            code: error.code,
            message: error.error_description,
            stack: error.method,
            data: {
              uri: uri,
            },
          },
        },
        'Failed to send update email request to AMC'
      );

      throw error;
    }
  }

  @serverOnly()
  public static async updateAMCPassword(
    oldPassword: string,
    newPassword: string,
    token: string
  ): Promise<AMCAccountServiceResponse> {
    const domain: string = amorphicStatic.config.nconf.get('amcAccountServiceDomain');
    const uri: string = `${domain}/api/update-password`;

    const options = {
      uri: uri,
      method: 'PUT',
      json: true,
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        'x-access-token': token,
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
      body: {
        oldPassword: oldPassword,
        newPassword: newPassword,
      },
    };

    try {
      const response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Client',
          function: 'updateAMCPassword',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.s1 },
          data: {
            token: token,
          },
        },
        `Sent request to AMC to update password`
      );

      return response;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Client',
          function: 'updateAMCPassword',
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              uri: uri,
            },
          },
        },
        'Failed to send update password request to AMC'
      );

      throw error;
    }
  }

  public static async setAMCEmailVerified(email: string): Promise<void> {
    if (!email) {
      return;
    }

    const cleanedEmail = querystring.escape(email);
    const lowerCaseEmail = cleanedEmail.toLowerCase();
    const uriSuffix = `v1/AmcUser/verifyemail?email=${lowerCaseEmail}`;

    const response = Auth0Client.sendAMCRequest(
      uriSuffix,
      Auth0RequestTypes.POST,
      'setAMCEmailVerified',
      'set email to verified with AMC'
    );
  }

  public static async resendAMCVerificationEmail(email: string): Promise<AMCResponse> {
    if (!email) {
      return null;
    }

    const cleanedEmail = querystring.escape(email);
    const lowerCaseEmail = cleanedEmail.toLowerCase();
    const uriSuffix = `v1/AmcUser/resendverification?email=${lowerCaseEmail}`;

    const response = Auth0Client.sendAMCRequest(
      uriSuffix,
      Auth0RequestTypes.POST,
      'resendAMCVerificationEmail',
      'resend AMC verification email'
    );

    return response;
  }

  // for testing - whitelist email with amc so user can recieve amc emails in that inbox
  public static async whiteListAMCemail(email: string) {
    if (!email) {
      return null;
    }

    const cleanedEmail = querystring.escape(email);
    const lowerCaseEmail = cleanedEmail.toLowerCase();
    const uriSuffix = `v1/AmcUser/emailwhitelist?email=${lowerCaseEmail}`;

    const response = Auth0Client.sendAMCRequest(
      uriSuffix,
      Auth0RequestTypes.POST,
      'whiteListAMCemail',
      'whitelist email with AMC'
    );

    return response;
  }

  // for testing - whitelist email with amc so user can recieve amc emails in that inbox
  public static async getHeavyUserAMCdata(amc_guid: string, auth0_id: string): Promise<AMCHeavyUserData> {
    if (!amc_guid || !auth0_id) {
      return null;
    }

    const uriSuffix = `v1/AmcUser/customerinfo?amc_id=${amc_guid}&auth0_id=${auth0_id}`;

    const response = Auth0Client.sendAMCRequest(
      uriSuffix,
      Auth0RequestTypes.GET,
      'getHeavyUserAMCdata',
      'get AMC heavy user data'
    );

    return response;
  }

  @serverOnly()
  private static async sendAMCRequest(
    uriSuffix: string,
    methodType: string,
    activity: string,
    requestDesc: string,
    suppressErrorHandler?: boolean
  ) {
    const auth0Conf: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.COVERPATH);
    let auth0APIToken: string = await Auth0Utils.handleAuth0Token(Auth0Channel.COVERPATH);

    const uri = `${auth0Conf.apiDomain}/rest/personalapi/${uriSuffix}`;

    let retryCount = 0;

    do {
      try {
        const options = {
          uri: uri,
          method: methodType,
          json: true,
          timeout: DEFAULT_REQUEST_TIMEOUT,
          headers: {
            'x-access-token': auth0APIToken,
            ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
          },
        };

        const response = await requestPromise(options);

        try {
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: activity,
              category: Category.request,
              context: { piiLevel: PiiLevelEnum.s2 },
              data: {
                uri: uri,
                response: response,
              },
            },
            `Request to ${requestDesc} succeeded`
          );
        } catch (loggingError) {
          LoggerSingleton.getInstance().error(
            {
              module: 'Auth0Client',
              function: activity,
              category: Category.request,
              context: { piiLevel: PiiLevelEnum.s2 },
              error: {
                name: loggingError?.name,
                message: loggingError?.message,
                stack: loggingError?.stack,
                isHumanRelated: false,
              },
            },
            `Error logging success for ${requestDesc}`
          );
        }

        return response;
      } catch (error) {
        if (
          (error.error.name === 'TokenExpiredError' || error.error.error === 'Unauthorized') &&
          retryCount < DEFAULT_RETRY_MAX
        ) {
          //perhaps redis has the wrong token. acquire a new API token and retry
          LoggerSingleton.getInstance().info(
            {
              module: 'Auth0Client',
              function: activity,
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
            },
            'Auth0 Token was not accepted. Retrying by acquiring new API Token'
          );

          //remove the current invalid token in redis
          await Auth0Utils.deleteAuth0TokenInCache(Auth0Channel.COVERPATH);
          auth0APIToken = await Auth0Client.acquireNewAPIToken(Auth0Channel.COVERPATH);
          retryCount = retryCount + 1;
        } else {
          if (suppressErrorHandler) {
            throw error;
          }
          LoggerSingleton.getInstance().error(
            {
              module: 'Auth0Client',
              function: activity,
              category: Category.request,
              context: { piiLevel: PiiLevelEnum.s2 },
              error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                isHumanRelated: false,
                data: {
                  uri: uri,
                },
              },
            },
            `Failed to ${requestDesc}`
          );
          //exit
          throw new Error(`Unable to ${requestDesc}`);
        }
      }
    } while (retryCount <= DEFAULT_RETRY_MAX);
  }
}

export interface Auth0Response {
  id_token?: string;
  access_token: string;
  scope: string;
  token_type: string;
  expires_in: number;
}
export interface AMCAuth0Response {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface AMCResponse {
  success: string;
  message: string;
}

export interface Auth0Error {
  text: string;
  code: string;
  mode?: string;
  provider?: string;
}

export interface Auth0UserMetadata {
  enable_mfa: boolean;
  mfa_email_sent: boolean;
}

export interface Auth0ResponseAccount {
  email: string;
  email_verified: boolean;
  updated_at: Date;
  name: string;
  picture: string;
  user_id: string;
  nickname: string;
  created_at: Date;
  last_ip: string;
  last_login: Date;
  logins_count: number;
  user_metadata: Auth0UserMetadata;
}

export interface AMCAuth0ResponseUser {
  user_id: string;
  user_type: string;
  user_amc_guid?: string;
  email_verified: boolean;
  mfa_verified: boolean;
}

export interface AMCAccountServiceResponse {
  responseCode: number;
  responseDesc: string;
  responseBody: string;
}

// TODO: prolly rename this
export interface AMCHeavyUserData {
  governmentId: string;
  birthDate: string;
  firstName: string;
  middleName: string;
  lastName: string;
  type: string;
}

export enum AMCuserType {
  LIGHT = 'light',
  HEAVY = 'heavy',
}
